import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ACHETEURS, ACTIVITES, CATEGORIES, DEPARTEMENTS, DIVISIONS, FOURNISSEURS, PRODUITS, ROLES, STATS_ACTIVITES, STATS_DIVISIONS, STATS_FOURNISSEURS, TACHES } from '../../constants/routes';
import { RootState } from '../store';
import { Division } from '../../models/Division';
import { Departement } from '../../models/Departement';
import { addNotificationMessage, logout, setShowModalProductNotFound } from '../slices/appStateSlice';
import { Categorie } from '../../models/Categorie';
import { Fournisseur } from '../../models/Fournisseur';
import { addTreeViewSettings } from '../../commons/helpers/TreeViewHelper';
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { Tache } from '../../models/Tache';
import { Role } from '../../models/Role';
import { Activite } from '../../models/Activite';

const baseQuery = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
    prepareHeaders: (headers: Headers, { getState }): Headers => {
        return setHeaders(headers, (getState() as RootState).appState.token);
    },
})
const baseQueryInterceptor: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    {},
    FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);
    if (result.error) {
        switch (result.error.status) {
            case 401:
            case "FETCH_ERROR":
                api.dispatch(logout());
                return { error: undefined, data: undefined, meta: undefined } as QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>;
            case 403:
                api.dispatch(addNotificationMessage({ text: `Vous ne possédez pas les autorisations nécessaires pour modifier cette donnée. (${result.meta?.response?.headers?.get('X-Applicat-Requestid')})`, style: 'error', key: Math.random(), timeout: 15000 }));
                return { error: undefined, data: undefined, meta: undefined } as QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>;
            case 404:
                if (typeof args === "string" && args.includes(PRODUITS))
                    api.dispatch(setShowModalProductNotFound(true))
                else
                    api.dispatch(addNotificationMessage({ text: `La donnée est inexistante. (${result.meta?.response?.headers?.get('X-Applicat-Requestid')})`, style: 'error', key: Math.random(), timeout: 15000 }));
                return { error: undefined, data: undefined, meta: undefined } as QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>;
            case 423:
                api.dispatch(addNotificationMessage({ text: `La donnée est présentement en modification par un autre utilisateur. (${result.meta?.response?.headers?.get('X-Applicat-Requestid')})`, style: 'error', key: Math.random(), timeout: 15000 }));
                return { error: undefined, data: undefined, meta: undefined } as QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>;
            default:
                api.dispatch(addNotificationMessage({ text: `Problème interne, veuillez communiquer avec le département TI. (${result.meta?.response?.headers?.get('X-Applicat-Requestid')})`, style: 'error', key: Math.random(), timeout: 15000 }));
                return { error: undefined, data: undefined, meta: undefined } as QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>;
        }
    } else if (api.type === 'mutation') {
        switch ((args as FetchArgs).method) {
            case "POST":
                api.dispatch(addNotificationMessage({ text: `L'ajout a été effectué avec succès!`, style: 'success', key: Math.random(), timeout: 3000 }));
                break;
            case "PUT":
                api.dispatch(addNotificationMessage({ text: `La mise à jour a été effectuée avec succès!`, style: 'success', key: Math.random(), timeout: 3000 }));
                break;
            case "DELETE":
                api.dispatch(addNotificationMessage({ text: `La suppression a été effectuée avec succès!`, style: 'success', key: Math.random(), timeout: 3000 }));
                break;
        }

    }

    return result
}

export const approbationWebAPI = createApi({
    reducerPath: 'approbationWebAPI',
    tagTypes: ["divisions", "fournisseurs", "fournisseursStats", "divisionsStats", "produits", "taches", "activites"],
    baseQuery: baseQueryInterceptor,
    endpoints: (builder) => ({
        getDivisions: builder.query<Division[], void>({
            async queryFn(_arg, _api, _extraOptions, baseQuery) {
                const divisions = await baseQuery(DIVISIONS);
                if (divisions.error)
                    return { data: undefined, error: divisions.error };
                const statsDivisions = await baseQuery(STATS_DIVISIONS);
                if (statsDivisions.error)
                    return { data: undefined, error: statsDivisions.error }
                return {
                    data: addTreeViewSettings((divisions.data as Division[]).reduce((accumulator: Division[], currentValue) => {
                        if ((statsDivisions.data as any[]).find(s => s.div_num === currentValue.div_num)) {
                            var newValue = { ...currentValue, stats: (statsDivisions.data as any[]).find(s => s.div_num === currentValue.div_num) }
                            accumulator.push(newValue);
                        }
                        return accumulator;
                    }, []), 'divisions')
                };
            },
            providesTags: ["divisions"]
        }),
        getDepartements: builder.query<Departement[], number>({
            async queryFn(div, _api, _extraOptions, baseQuery) {
                const departements = await baseQuery(`${DEPARTEMENTS}${getUrlParams({ div })}`);
                if (departements.error)
                    return { data: undefined, error: departements.error };
                const statsDepartements = await baseQuery(`${STATS_DIVISIONS}/${div}`);
                if (statsDepartements.error)
                    return { data: undefined, error: statsDepartements.error }
                return {
                    data: addTreeViewSettings((departements.data as Departement[]).reduce((accumulator: Departement[], currentValue) => {
                        if ((statsDepartements.data as any[]).find(s => s.dept_cd === currentValue.dept_cd)) {
                            var newValue = { ...currentValue, stats: (statsDepartements.data as any[]).find(s => s.dept_cd === currentValue.dept_cd) }
                            accumulator.push(newValue);
                        }
                        return accumulator;
                    }, []), 'departements')
                };
            },
        }),
        getCategories: builder.query<Categorie[], { div: number, dept: string, four: string }>({
            async queryFn({ div, dept, four }, _api, _extraOptions, baseQuery) {
                const categories = await baseQuery(`${CATEGORIES}${getUrlParams({ div, dept, four })}`);
                if (categories.error)
                    return { data: undefined, error: categories.error };
                const statsCategories = await baseQuery(four ? `${STATS_FOURNISSEURS}/${four}` : `${STATS_DIVISIONS}/${div}/departement/${dept}`);
                if (statsCategories.error)
                    return { data: undefined, error: statsCategories.error }
                return {
                    data: addTreeViewSettings((categories.data as Categorie[]).reduce((accumulator: Categorie[], currentValue) => {
                        if ((statsCategories.data as any[]).find(s => s.cat_cd === currentValue.cat_cd)) {
                            var newValue = { ...currentValue, stats: (statsCategories.data as any[]).find(s => s.cat_cd === currentValue.cat_cd) }
                            accumulator.push(newValue);
                        }
                        return accumulator;
                    }, []), 'categories')
                };
            },
        }),
        getFournisseurs: builder.query<Fournisseur[], void>({
            async queryFn(_arg, _api, _extraOptions, baseQuery) {
                const fournisseurs = await baseQuery(`${FOURNISSEURS}?withMod=true`);
                if (fournisseurs.error)
                    return { data: undefined, error: fournisseurs.error };
                const statsFournisseurs = await baseQuery(STATS_FOURNISSEURS);
                if (statsFournisseurs.error)
                    return { data: undefined, error: statsFournisseurs.error }
                return {
                    data: addTreeViewSettings((fournisseurs.data as Fournisseur[]).reduce((accumulator: Fournisseur[], currentValue) => {
                        if ((statsFournisseurs.data as any[]).find(s => s.four_cd === currentValue.four_cd)) {
                            var newValue = { ...currentValue, stats: (statsFournisseurs.data as any[]).find(s => s.four_cd === currentValue.four_cd) }
                            accumulator.push(newValue);
                        }
                        return accumulator;
                    }, []), 'fournisseurs')
                };
            },
            providesTags: ["fournisseurs"]
        }),
        getActivites: builder.query<Activite[], void>({
            async queryFn(_arg, _api, _extraOptions, baseQuery) {
                const activites = await baseQuery(`${ACTIVITES}`)
                if (activites.error)
                    return { data: undefined, error: activites.error };
                const statsActivites = await baseQuery(STATS_ACTIVITES);
                if (statsActivites.error)
                    return { data: undefined, error: statsActivites.error }
                return {
                    data: addTreeViewSettings((activites.data as Activite[]).reduce((accumulator: Activite[], currentValue) => {
                        if ((statsActivites.data as any[]).find(s => s.act_cd === currentValue.act_cd)) {
                            var newValue = { ...currentValue, stats: (statsActivites.data as any[]).find(s => s.act_cd === currentValue.act_cd) }
                            accumulator.push(newValue);
                        }
                        return accumulator;
                    }, []), 'activites')
                };
            },
            providesTags: ["activites"]
        }),
        getTaches: builder.query<Tache[], void>({
            query: () => TACHES,
            transformResponse(taches: Tache[]) {
                return addTreeViewSettings(taches, 'taches')
            },
            providesTags: ["taches"]
        }),
        getProduits: builder.query<any, { div: number, dept: string, cat: string, four: string, page: number, pagesize: number, taches: string, tacheId: number, ptr: string, acht: string, approuvefra: boolean | null, approuveang: boolean | null, activite: number }>({
            query: ({ div, dept, cat, four, page, pagesize, taches, tacheId, ptr, acht, approuvefra, approuveang, activite }) => ptr ? `${PRODUITS}/${ptr}` : `${PRODUITS}${getUrlParams({ div, dept, cat, four, page, pagesize, taches, tacheId, acht, approuvefra, approuveang, activite })}`,
            transformResponse(apiResponse, meta: any, args: any) {
                if (meta)
                    return { data: args.ptr ? [apiResponse] : apiResponse, totalCount: Number(meta.response.headers.get('X-Total-Count')) }
                else
                    return { data: [], totalCount: 0 }
            },
            providesTags: ["produits"]
        }),
        getStatsDepartements: builder.query<any, number>({
            query: (div_num) => `${STATS_DIVISIONS}${getUrlParams({ div_num })}`
        }),
        getStatsCategoriesFournisseurs: builder.query<any, string>({
            query: (four_cd) => `${STATS_FOURNISSEURS}${getUrlParams({ four_cd })}`
        }),
        getStatsCategoriesDivisions: builder.query<any, { div_num: number, dept_cd: string }>({
            query: ({ div_num, dept_cd }) => `${STATS_DIVISIONS}${getUrlParams({ div_num, dept_cd })}`
        }),
        getRoles: builder.query<Role[], void>({
            query: () => ROLES
        }),
        getAcheteurs: builder.query<any, void>({
            query: () => `${ACHETEURS}?fonction=Acheteur&Sort=usr_nom`
        }),
        getTachesAutomatisees: builder.query<Tache[], void>({
            query: () => `${TACHES}?type=A&calcul=false`
        }),
        addTache: builder.mutation<any, { div: number, dept: string, cat: string, four: string, tacheId: number, prodNum: string, acht: string, activite: number, approuveang: boolean | null, approuvefra: boolean | null, taches: string, body: any }>({
            query: ({ div, dept, cat, four, tacheId, prodNum, acht, activite, approuveang, approuvefra, taches, body }) => ({
                url: `${TACHES}${getUrlParams({ div, dept, cat, four, tacheId, prodNum, activite, acht, approuveang, approuvefra, taches })}`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ["taches"]
        }),
        updateProduit: builder.mutation({
            query: (body) => ({
                url: `${PRODUITS}/${body.prod_num}`,
                method: 'PUT',
                body: body,
            }),
        }),
        updateProduitApprobation: builder.mutation({
            query: (body) => ({
                url: `${PRODUITS}/${body.prod_num}/approbation`,
                method: 'PUT',
                body: body,
            }),
        }),
        updateTache: builder.mutation({
            query: (tacheId) => ({
                url: `${TACHES}/${tacheId}`,
                method: 'PUT',
                body: { analyser: true }
            }),
            invalidatesTags: ["produits", "taches"]
        }),
        deleteTache: builder.mutation<any, { tacheId: number, prod_num: number }>({
            query: ({ tacheId, prod_num }) => ({
                url: prod_num ? `${TACHES}/${tacheId}/${prod_num}` : `${TACHES}/${tacheId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ["taches"]
        })
    }),
})

function setHeaders(headers: Headers, token: string): Headers {
    headers.set('Content-Type', 'application/json');
    headers.set('Authorization', token);
    headers.set('x-applicat-apikey', process.env.REACT_APP_API_KEY!)
    return headers;
}

function getUrlParams(args: any) {
    let parameters = '';
    let operateur = '?';
    Object.keys(args).forEach(arg => {
        if ((args[arg] || args[arg] === 0 || args[arg] === false) && args[arg] !== -1) {
            parameters = parameters.concat(`${operateur}${arg}=${args[arg]}`);
            operateur = operateur === '?' ? '&' : operateur;
        }
    });

    return parameters;
}

export const {
    useGetDivisionsQuery,
    useGetFournisseursQuery,
    useLazyGetCategoriesQuery,
    useGetProduitsQuery,
    useLazyGetDepartementsQuery,
    useGetStatsCategoriesDivisionsQuery,
    useGetStatsCategoriesFournisseursQuery,
    useGetStatsDepartementsQuery,
    useGetTachesQuery,
    useGetTachesAutomatiseesQuery,
    useGetActivitesQuery,
    useUpdateProduitMutation,
    useUpdateProduitApprobationMutation,
    useLazyGetProduitsQuery,
    useGetRolesQuery,
    useGetAcheteursQuery,
    useGetDepartementsQuery,
    useGetCategoriesQuery,
    useDeleteTacheMutation,
    useAddTacheMutation,
    useUpdateTacheMutation
} = approbationWebAPI;